import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select, { components, MenuListProps } from "react-select";
import GroupLoginCarouselControl from "./GroupLoginCarouselControl"; // your generic carousel

const darkLogo = `${process.env.REACT_APP_IMAGE_BASEPATH}/dark-logo.svg`;

type Institute = {
  id: string | number;
  institute_id?: string | number;
  name: string;
  display_name?: string;
  domain: string;
  logo?: string | null;
};

type Props = {
  brandName: string;          // e.g., "Ryan", "Euro", "VIT"
  logoUrl: string;            // brand logo
  groupId: string;            // institute_group_id from backend
  headline?: string;          // optional custom H2
};

const GroupLayout: React.FC<Props> = ({ brandName, logoUrl, groupId, headline }) => {
  const navigate = useNavigate();

  const [institutes, setInstitutes] = useState<Institute[]>([]);
  const [loadingInstitutes, setLoadingInstitutes] = useState(false);
  const [instituteError, setInstituteError] = useState<string | null>(null);

  const [selectedDomain, setSelectedDomain] = useState<string>("");
  const [selectedInstituteId, setSelectedInstituteId] = useState<string | number | null>(null);

  // Limit react-select menu to 10 items
  const MenuList = (props: MenuListProps<any>) => {
    const { children } = props;
    const limited = Array.isArray(children) ? (children as any[]).slice(0, 10) : children;
    return <components.MenuList {...props}>{limited}</components.MenuList>;
  };

  useEffect(() => {
    const fetchInstitutes = async () => {
      try {
        setLoadingInstitutes(true);
        setInstituteError(null);

        const url = `${process.env.REACT_APP_BROADCAST_SERVER}/pre-signup/institute-data-by-grouid`;
        const res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ id: groupId }),
        });

        if (!res.ok) {
          let details = "";
          try { details = await res.text(); } catch {}
          throw new Error(`HTTP ${res.status}${details ? ` - ${details}` : ""}`);
        }

        const data = await res.json();
        const list: Institute[] = Array.isArray(data?.result) ? data.result : [];
        setInstitutes(list);
      } catch (e: any) {
        setInstituteError(e?.message || "Failed to load institutes");
      } finally {
        setLoadingInstitutes(false);
      }
    };

    fetchInstitutes();
  }, [groupId]);

  const instituteOptions = useMemo(
    () =>
      institutes.map((inst) => ({
        value: inst.id ?? inst.institute_id,
        label: inst.name,
        meta: inst,
      })),
    [institutes]
  );

  const handleNext = () => {
    if (!selectedDomain) return;
    const domain = String(selectedDomain).trim().replace(/^https?:\/\//i, "").replace(/\/+$/, "");
    navigate(`/user/${encodeURIComponent(domain)}/login`);
  };

  return (
    <div className="container-fluid bg_side_blue landing_login_page d-flex flex-1">
      <div className="container max-1300 flex-1">
        <div className="row h-100 position-relative z1">
          {/* Left column: carousel */}
          <div className="col-md-6 d-none d-md-flex flex-column">
            <div className="leftPaneMain">
              {/* pass brand props to your generic carousel */}
              <GroupLoginCarouselControl brandName={brandName} logoUrl={logoUrl} />
            </div>
            <div className="leftPaneFooter">
              <div className="row g-4 align-items-center">
                <div className="col-md">
                  <div className="copyRight">
                    International Educational Gateway Pvt. Ltd. © 2025
                    <span>All Rights Reserved . Univariety</span>
                  </div>
                </div>
                <div className="col-md-auto">
                  <div className="powerby">
                    <span>Powered By</span>
                    <img src={darkLogo} alt="" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right column: logo + title + select + next */}
          <div className="col-md-6">
            <div className="signup h-100" aria-autocomplete="none">
              <div className="max-400 mx-auto mt-5">
                <div className="row g-4 flex-column align-items-center text-white">
                  <div className="col-auto">
                    <img src={logoUrl} width="100" className="img-fluid mx-auto d-block" alt={`${brandName} logo`} />
                  </div>

                  <div className="col-12 text-center">
                    <div className="welcome_text mb-3">
                      <h2 className="font-600 font-24 mb-2">
                        {headline ?? `${brandName} International Group of Institutions`}
                      </h2>
                    </div>
                  </div>

                  <div className="col-md-12 group-login">
                    <label className="form-label w-100 font-500 mb-1 text-white">Select Institution</label>

                    <Select
                      inputId="college-group"
                      classNamePrefix="react-select"
                      isSearchable
                      isClearable
                      isLoading={loadingInstitutes}
                      options={instituteOptions}
                      components={{ MenuList }}
                      placeholder={loadingInstitutes ? "Loading…" : "Search by name or domain…"}
                      filterOption={(option, input) => {
                        const term = (input || "").toLowerCase();
                        const label = String(option.label || "").toLowerCase();
                        const domain = String(option.data?.meta?.domain || "").toLowerCase();
                        return label.includes(term) || domain.includes(term);
                      }}
                      onChange={(opt: any) => {
                        setSelectedInstituteId(opt ? opt.value : null);
                        setSelectedDomain(opt?.meta?.domain || "");
                      }}
                    />

                    {instituteError && <small className="text-warning d-block mt-2">{instituteError}</small>}
                  </div>

                  <div className="col-md-12">
                    <div className="mt-4 mb-3 d-flex justify-content-center" id="buttonPallet">
                      <button className="btn-green px-5" disabled={!selectedDomain} onClick={handleNext}>
                        <span>Next</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* /Right */}
        </div>
      </div>
    </div>
  );
};

export default GroupLayout;
